// src/modals/LiensVoyageurSection.jsx — CHANTIER LIEN COURT (côté admin).
//
// Bloc « Liens voyageur » de la fiche réservation (onglet Documents) : les liens
// que Dimitri envoie À LA MAIN à un voyageur (copier-coller, dicté au
// téléphone). Jusqu'ici SEUL le lien de formulaire était copiable ; l'espace
// séjour, le livret et le reçu n'avaient AUCUN bouton.
//
// Chaque bouton passe par `guestLinkUrl()` → lien COURT
// `sejour.fullbnbzen.com/l/<code>` (émission idempotente serveur), avec REPLI
// automatique sur le lien LONG si l'émission échoue.
//
// ⚠️ LE LIEN DE PAIEMENT N'EST PAS ICI — volontairement. Il reste dans l'onglet
// Paiements, en lien long (le chemin argent n'est jamais aliasé : triple
// barrière GUEST_LINK_KINDS + CHECK SQL + routeur front). Ne pas l'ajouter.
//
// SOURCES DES JETONS (aucune création ici — on aliase l'existant) :
//   - form_tokens rattachés à l'item (reservation_id = item.id, même convention
//     polymorphique que listFormTokensByReservation : reservations.id pour une
//     résa OTA, pricing_blocks.id pour une résa directe) :
//       unified/welcome/checkin → kind `form`   (fiche d'arrivée)
//       sejour_ota              → kind `journey_ota` (espace séjour OTA)
//       livret                  → kind `livret`
//       doc_receipt             → kind `recu`
//   - direct_booking_requests.journey_token → kind `journey` (espace séjour
//     Direct), lu par pricing_block_id pour une résa directe.
//
// Un lien absent = jeton pas encore émis par le parcours (le moteur les crée
// paresseusement au premier envoi). On affiche alors une ligne grisée
// explicite plutôt qu'un bouton qui copierait un lien mort.

import { useEffect, useState } from "react"
import { Link2 } from "lucide-react"
import { supabase } from "../supabase"
import { DS } from "../lib/ui"
import { listFormTokensByReservation } from "../lib/forms-api"
import CopyGuestLinkButton from "../components/CopyGuestLinkButton"

// form_tokens.type → kind du hub `/l/`. Les types `payment_caution` /
// `payment_sejour` sont ABSENTS de cette table : un lien de paiement ne peut
// pas remonter ici (barrière côté client, doublée serveur).
const KIND_BY_TOKEN_TYPE = {
  unified: "form",
  welcome: "form",
  checkin: "form",
  sejour_ota: "journey_ota",
  livret: "livret",
  doc_receipt: "recu",
}

// Ordre d'affichage + libellés (l'espace séjour d'abord : c'est le lien le plus
// souvent envoyé à la main).
const ROWS = [
  {
    kind: "journey",
    label: "Espace séjour (Direct)",
    hint: "Parcours de la réservation directe (contrat, paiement, caution, livret).",
  },
  {
    kind: "journey_ota",
    label: "Espace séjour (OTA)",
    hint: "Espace voyageur des réservations Airbnb / Booking.",
  },
  {
    kind: "form",
    label: "Fiche d'arrivée",
    hint: "Formulaire de bienvenue (identité, heures d'arrivée).",
  },
  { kind: "livret", label: "Livret d'accueil", hint: "Livret du logement." },
  { kind: "recu", label: "Reçu du séjour", hint: "Document figé du séjour." },
]

export default function LiensVoyageurSection({ item }) {
  const itemId = item && item.id ? item.id : null
  const isBlock = item && item.kind === "pricing_block"
  const [tokensByKind, setTokensByKind] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const found = {}
      if (!itemId) {
        if (!cancelled) { setTokensByKind({}); setLoading(false) }
        return
      }
      // 1. form_tokens de l'item (triés created_at DESC → 1er vu = le plus récent).
      try {
        const rows = await listFormTokensByReservation(itemId)
        for (const row of rows || []) {
          const kind = KIND_BY_TOKEN_TYPE[row && row.type]
          if (!kind || found[kind]) continue
          found[kind] = row.token
        }
      } catch (e) {
        // Lecture impossible → aucune ligne proposée pour ces kinds (jamais un
        // bouton qui copierait un lien inventé). Pas de catch muet.
        console.error("[LiensVoyageurSection] form_tokens:", e && e.message ? e.message : e)
      }
      // 2. journey_token de la demande directe (résa directe = pricing_block).
      if (isBlock) {
        try {
          const { data, error } = await supabase
            .from("direct_booking_requests")
            .select("journey_token")
            .eq("pricing_block_id", itemId)
            .not("journey_token", "is", null)
            .limit(1)
          if (error) throw error
          const row = Array.isArray(data) ? data[0] : null
          if (row && row.journey_token) found.journey = row.journey_token
        } catch (e) {
          console.error("[LiensVoyageurSection] journey_token:", e && e.message ? e.message : e)
        }
      }
      if (!cancelled) { setTokensByKind(found); setLoading(false) }
    })()
    return () => { cancelled = true }
  }, [itemId, isBlock])

  // On n'affiche que les lignes PERTINENTES pour cet item : celles dont le
  // jeton existe. Si aucune n'existe, la section reste rendue avec un message
  // (l'admin comprend qu'il n'y a rien à copier, pas que le bloc a disparu).
  const rows = ROWS.filter((r) => tokensByKind[r.kind])

  return (
    <div data-testid="liens-voyageur-section" style={{ marginBottom: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: DS.fontDisplay,
          fontWeight: 600,
          color: DS.marineDark,
          fontSize: 14,
          marginBottom: 8,
        }}
      >
        <Link2 size={16} strokeWidth={2} />
        Liens voyageur
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: DS.textMuted }}>Chargement des liens…</div>
      ) : rows.length === 0 ? (
        <div data-testid="liens-voyageur-empty" style={{ fontSize: 12, color: DS.textMuted }}>
          Aucun lien voyageur généré pour cette réservation. Les liens sont créés
          par le parcours (envoi du formulaire, de l'espace séjour ou du reçu).
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((r) => (
            <div
              key={r.kind}
              data-testid={`lien-voyageur-${r.kind}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                background: "#f7f9fc",
                border: "1px solid #e6ecf4",
                borderRadius: 8,
                padding: "8px 10px",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: DS.marineDark }}>{r.label}</div>
                <div style={{ fontSize: 10, color: DS.textMuted }}>{r.hint}</div>
              </div>
              <CopyGuestLinkButton
                kind={r.kind}
                token={tokensByKind[r.kind]}
                label="Copier le lien"
                testid={`copy-guest-link-${r.kind}`}
                compact
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
